import { bcLong } from '@lib2/model/util/string/BaseConverter';
import {
  LIST_TYPE_LABELS, LIST_SORT_UI_OPTIONS,
  PLAYLIST_MEDIA_NOUNS, PLAYLIST_MEDIA_NOUN_FALLBACK,
} from '@/lib/constants/playlist';
import type { PlaylistMediaNoun } from '@/lib/constants/playlist';
import type { MediaStateType, MediaType } from '@/lib/types/Media';
import type {
  AnyPlaylistData, AnyPlaylistView, CustomPlaylistData, CustomPlaylistView,
  TagPlaylistData, TagPlaylistView, PlaylistMediaView,
  PlaylistGridRow, PlaylistListResponse, PlaylistMediaPosition,
  ListTagRule, TagSearchRule,
  PlaylistSettingsFormData, TagPlaylistContentFormData,
  UpdateCustomPlaylistRequest, UpdateTagPlaylistRequest,
} from '@/lib/types/Playlist';
import type { TagLogicalOperator } from '@/lib/constants/playlist';

export class PlaylistUtils {

  /** UI noun set for a playlist's items ("video"/"live stream"), with a generic fallback for legacy mediaTypes. */
  static getMediaNoun(mediaType: MediaType): PlaylistMediaNoun {
    return PLAYLIST_MEDIA_NOUNS[mediaType] ?? PLAYLIST_MEDIA_NOUN_FALLBACK;
  }

  // --- Grid transforms ---

  /**
   * Core tag-rule matcher: count media items satisfying and/or/not rules,
   * filtered by mediaType as a safety net, capped by limit when set.
   */
  static countTagMatches(
    source: PlaylistMediaView[],
    mediaType: MediaType,
    tagRules: ListTagRule[],
    limit: number | null
  ): number {
    const candidates = source.filter(v => v.media.mediaType === mediaType);

    const andTags = tagRules.filter(r => r.rule === 'and').map(r => r.tag).filter(Boolean);
    const orTags = tagRules.filter(r => r.rule === 'or').map(r => r.tag).filter(Boolean);
    const notTags = tagRules.filter(r => r.rule === 'not').map(r => r.tag).filter(Boolean);

    const matched = candidates.filter(item => {
      const itemTags = item.tags || [];
      if (andTags.length > 0 && !andTags.every(t => itemTags.includes(t))) return false;
      if (orTags.length > 0 && !orTags.some(t => itemTags.includes(t))) return false;
      if (notTags.length > 0 && notTags.some(t => itemTags.includes(t))) return false;
      return true;
    }).length;

    return limit != null ? Math.min(matched, limit) : matched;
  }

  /**
   * Compute the approximate number of media items matching a tag playlist's rules.
   * Applies tag rule logic (and/or/not) against cached media data (videos or streams,
   * per the playlist's mediaType).
   * Returns null if the source data is not yet loaded.
   */
  static computeTagMatchCount(playlist: TagPlaylistView, source: PlaylistMediaView[] | undefined): number | null {
    if (!source) return null;
    return PlaylistUtils.countTagMatches(source, playlist.mediaType, playlist.tagRules, playlist.limit);
  }

  /**
   * Live match count for the smart playlist edit form: evaluates the current
   * (possibly unsaved) grouped rules and limit input against cached media data.
   * An invalid limit input (still being typed / failing validation) is treated
   * as no limit so the count stays meaningful while the field shows its error.
   * Returns null if the source data is not yet loaded.
   */
  static computeFormMatchCount(
    mediaType: MediaType,
    rules: TagSearchRule[],
    limitInput: string,
    source: PlaylistMediaView[] | undefined
  ): number | null {
    if (!source) return null;
    const flat = PlaylistUtils.searchRulesToTagRules(rules);
    const parsed = Number(limitInput);
    const limit = limitInput.trim() !== '' && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    return PlaylistUtils.countTagMatches(source, mediaType, flat, limit);
  }

  /** Convert a single playlist to a grid row for display */
  static toGridRow(playlist: AnyPlaylistView, videos?: PlaylistMediaView[], streams?: PlaylistMediaView[]): PlaylistGridRow {
    const tagSource = playlist.mediaType === 'video_stream' ? streams : videos;
    const itemCount = playlist.listType === 'custom'
      ? (playlist as CustomPlaylistView).mediaPositions.length
      : PlaylistUtils.computeTagMatchCount(playlist as TagPlaylistView, tagSource);
    return {
      listId: playlist.listId,
      displayId: bcLong.getStr(playlist.listId),
      title: playlist.title,
      listType: playlist.listType,
      listTypeLabel: LIST_TYPE_LABELS[playlist.listType],
      state: playlist.mediaStateType,
      mediaType: playlist.mediaType,
      itemCount,
      created: playlist.created,
      updated: playlist.updated,
      isActive: playlist.mediaStateType === 'active',
    };
  }

  /**
   * Merge customLists + tagLists into a single array of grid rows.
   * Pass videos and/or streams (from the useVideos()/useStreams() caches) to compute
   * tag playlist match counts — each tag playlist counts against the source matching
   * its mediaType.
   */
  static toGridRows(response: PlaylistListResponse, videos?: PlaylistMediaView[], streams?: PlaylistMediaView[]): PlaylistGridRow[] {
    const allPlaylists: AnyPlaylistView[] = [
      ...response.customLists,
      ...response.tagLists,
    ];
    return allPlaylists.map(p => PlaylistUtils.toGridRow(p, videos, streams));
  }

  // --- Tag rule conversion ---

  /**
   * Convert flat backend format [{tag, rule}...] to grouped UI format [{lop, values[]}...]
   * Re-implements TagListView2.getTagRules() from lib2 as a pure function.
   */
  static tagRulesToSearchRules(flat: ListTagRule[]): TagSearchRule[] {
    const grouped = new Map<string, string[]>();
    for (const { tag, rule } of flat) {
      const existing = grouped.get(rule);
      if (existing) {
        if (tag) existing.push(tag);
      } else {
        grouped.set(rule, tag ? [tag] : []);
      }
    }
    return Array.from(grouped.entries()).map(([lop, values]) => ({
      lop: lop as TagLogicalOperator,
      values,
    }));
  }

  /**
   * Convert grouped UI format [{lop, values[]}...] back to flat [{tag, rule}...] for API.
   * Empty values arrays produce {rule: lop, tag: ''} to preserve the rule in the backend.
   * Re-implements TagListView2.setTagRules() from lib2 as a pure function.
   */
  static searchRulesToTagRules(grouped: TagSearchRule[]): ListTagRule[] {
    const flat: ListTagRule[] = [];
    for (const { lop, values } of grouped) {
      if (values.length === 0) {
        flat.push({ tag: '', rule: lop });
      } else {
        for (const tag of values) {
          flat.push({ tag, rule: lop });
        }
      }
    }
    return flat;
  }

  // --- Sort config conversion ---

  /** Combine sortType + sortDirection into a single UI key */
  static toSortUIType(sortType: string, sortDirection: string): string {
    return `${sortType}_${sortDirection}`;
  }

  /** Split a UI key back into sortType + sortDirection */
  static fromSortUIType(uiType: string): { sortType: string; sortDirection: string } {
    const option = LIST_SORT_UI_OPTIONS.find(o => o.value === uiType);
    if (option) return { sortType: option.sortType, sortDirection: option.sortDirection };
    return { sortType: 'published', sortDirection: 'desc' };
  }

  // --- Form data transforms ---

  /** Convert playlist data to settings form data (null → '' for form inputs) */
  static toSettingsFormData(playlist: AnyPlaylistData): PlaylistSettingsFormData {
    return {
      title: playlist.title,
      descShort: playlist.descShort ?? '',
      refId: playlist.refId ?? '',
      mediaStateType: playlist.mediaStateType as 'active' | 'inactive',
    };
  }

  /** Extract tag-specific content form data from a tag playlist */
  static toTagContentFormData(playlist: TagPlaylistData): TagPlaylistContentFormData {
    return {
      tagRules: PlaylistUtils.tagRulesToSearchRules(playlist.tagRules),
      sortUIType: PlaylistUtils.toSortUIType(playlist.sortType, playlist.sortDirection),
      limit: playlist.limit != null ? String(playlist.limit) : '',
    };
  }

  /** Build a full custom playlist update request from form data */
  static buildCustomUpdateRequest(
    settings: PlaylistSettingsFormData,
    mediaPositions: PlaylistMediaPosition[]
  ): UpdateCustomPlaylistRequest {
    return {
      mediaState: settings.mediaStateType as MediaStateType,
      title: settings.title,
      descShort: settings.descShort || null,
      refId: settings.refId || null,
      mediaPositions,
    };
  }

  /** Build a full tag playlist update request from form data */
  static buildTagUpdateRequest(
    settings: PlaylistSettingsFormData,
    content: TagPlaylistContentFormData
  ): UpdateTagPlaylistRequest {
    const { sortType, sortDirection } = PlaylistUtils.fromSortUIType(content.sortUIType);
    return {
      mediaState: settings.mediaStateType as MediaStateType,
      title: settings.title,
      descShort: settings.descShort || null,
      refId: settings.refId || null,
      limit: content.limit ? Number(content.limit) : null,
      sortType,
      sortDirection,
      tagRules: PlaylistUtils.searchRulesToTagRules(content.tagRules),
    };
  }

  // --- Type guards ---

  static isCustomPlaylist(p: AnyPlaylistData): p is CustomPlaylistData {
    return p.listType === 'custom';
  }

  static isTagPlaylist(p: AnyPlaylistData): p is TagPlaylistData {
    return p.listType === 'tag';
  }
}
